import { memo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Dropdown } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { Download, MoreHorizontal, Upload, UserPlus } from 'lucide-react';

import { PERMISSIONS } from '../../constants/permissions';
import Can from '../guards/Can';
import MenuAction from '../shared/MenuAction';

/**
 * Header actions of the Members tab, as in the prototype: a More actions menu (Export CSV), the
 * secondary Add from file button and the primary Invite members button. The last two are hidden
 * without invite permission. Under the `sm` breakpoint they keep only their icon (the accessible
 * name stays) so the title and the buttons fit one row.
 * Links are relative so they work embedded in the Core Shell.
 *
 * @param {object} props
 * @param {() => void} props.onExport
 * @param {'sm'|'md'} [props.size='md'] Button size; `sm` while the header is compressed.
 */
function MembersHeaderActions({ onExport, size = 'md' }) {
  const { t } = useTranslation('people-access');
  const navigate = useNavigate();
  const goToImport = useCallback(() => navigate('import'), [navigate]);
  const goToInvite = useCallback(() => navigate('invite'), [navigate]);

  return (
    <div className="flex items-center gap-2">
      <Dropdown
        variant="tertiary"
        size={size === 'sm' ? 'small' : 'medium'}
        alignMenu="right"
        triggerIcon={MoreHorizontal}
        triggerAriaLabel={t('members.actions.more')}
      >
        <MenuAction label={t('members.actions.exportCsv')} icon={Download} onSelect={onExport} />
      </Dropdown>
      <Can permission={PERMISSIONS.MEMBERS_INVITE}>
        <Button
          variant="secondary"
          size={size}
          leftIcon={Upload}
          aria-label={t('members.actions.addFromFile')}
          onClick={goToImport}
        >
          <span className="hidden sm:inline">{t('members.actions.addFromFile')}</span>
        </Button>
      </Can>
      <Can permission={PERMISSIONS.MEMBERS_INVITE}>
        <Button
          variant="primary"
          size={size}
          leftIcon={UserPlus}
          aria-label={t('members.actions.invite')}
          onClick={goToInvite}
        >
          <span className="hidden sm:inline">{t('members.actions.invite')}</span>
        </Button>
      </Can>
    </div>
  );
}

export default memo(MembersHeaderActions);
